import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Radar, Gavel, Sparkles, TrendingUp, ShieldCheck, MapPin, Timer } from "lucide-react";

export default function Landing() {
  const [preview, setPreview] = useState([]);
  const [stats] = useState({ analyzed: "R$ 428M+", opps: 1240, juntas: 27 });

  useEffect(() => {
    api.get("/vehicles/preview?limit=4").then((r) => setPreview(r.data.vehicles)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-xs font-mono uppercase tracking-widest mb-6" data-testid="hero-eyebrow">
              <Sparkles className="w-3.5 h-3.5" />
              Inteligência Judicial · Tabela FIPE · IA
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight" data-testid="hero-title">
              Encontre leilões judiciais com até <span className="text-emerald-400">60% abaixo</span> da tabela FIPE.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Cruzamos dados de leiloeiros oficiais registrados nas juntas comerciais de todo o Brasil e destacamos oportunidades com <strong className="text-purple-300">25% de entrada + 30 boletos</strong>, comparando lance mínimo, custas do edital e valor de mercado.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold px-6 h-12" data-testid="hero-cta-signup">
                  Começar com 10 créditos grátis
                </Button>
              </Link>
              <Link to="/search">
                <Button size="lg" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 h-12" data-testid="hero-cta-explore">
                  Ver leilões ativos
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
              {[
                { label: "Analisado", value: stats.analyzed },
                { label: "Oportunidades", value: stats.opps.toLocaleString("pt-BR") },
                { label: "Juntas Comerciais", value: stats.juntas },
              ].map((s) => (
                <div key={s.label} className="border-l-2 border-emerald-500/40 pl-4">
                  <div className="font-mono text-xl font-bold text-white">{s.value}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Preview cards */}
          <div className="mt-16">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Oportunidades em destaque</h2>
                <p className="text-sm text-slate-400 mt-1">Ranked por score de IA · atualizado agora</p>
              </div>
              <Link to="/search" className="text-sm text-emerald-400 hover:text-emerald-300 hidden sm:inline" data-testid="hero-see-all">
                Ver todas →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {preview.map((v) => (
                <div key={v.id} className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden card-hover" data-testid={`preview-card-${v.id}`}>
                  <div className="aspect-video bg-slate-800 relative">
                    <img src={v.image} alt={v.model} className="w-full h-full object-cover opacity-90" />
                    <div className="absolute top-2 left-2 deal-score">{v.score}/100</div>
                    {v.payment_25_30 && <div className="absolute top-2 right-2 badge-parcel"><Gavel className="w-3 h-3" />25% + 30x</div>}
                  </div>
                  <div className="p-4">
                    <div className="text-xs font-mono text-slate-400 uppercase tracking-widest flex items-center gap-1"><MapPin className="w-3 h-3" />{v.uf}</div>
                    <div className="mt-1 font-semibold text-white truncate">{v.brand} {v.model}</div>
                    <div className="text-xs text-slate-400">{v.year}</div>
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Lance mínimo</div>
                        <div className="font-mono text-emerald-400 font-bold">{brl(v.min_bid)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">FIPE</div>
                        <div className="font-mono text-slate-300 text-sm line-through">{brl(v.fipe_value)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-slate-800/60 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white text-center max-w-2xl mx-auto">Como o LeilãoRadar te dá vantagem</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Radar, title: "Cobertura nacional", body: "Monitoramos leiloeiros oficiais registrados nas juntas comerciais de todos os 27 estados." },
              { icon: TrendingUp, title: "Comparativo FIPE", body: "Somamos comissão, custas, pátio e IPVA — e comparamos ao valor real de mercado." },
              { icon: ShieldCheck, title: "IA de risco", body: "Cada lote recebe um score 0-100 e um veredicto claro: compre, analise ou evite." },
            ].map((f) => (
              <div key={f.title} className="p-6 rounded-xl bg-slate-900/60 border border-slate-800 card-hover">
                <div className="w-11 h-11 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-slate-800/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Timer className="w-10 h-10 text-amber-400 mx-auto" />
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-white">Praças acontecem toda semana.</h2>
          <p className="mt-4 text-slate-400">Cadastre-se em 30 segundos, ganhe 10 créditos e descubra sua próxima aquisição inteligente.</p>
          <div className="mt-8">
            <Link to="/register">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold h-12 px-8" data-testid="cta-final-signup">
                Criar conta grátis
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
